import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  School,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  Plus,
  Edit2,
  Trash2,
  FileText,
  Upload,
  Stamp,
  X,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { LivretConfig } from '../../types/supabase';

type VoieFormation = 'apprentissage' | 'initial' | 'formation_continue';

type SessionPrecedenteRow = {
  annee: number;
  presentes: number;
  recus: number;
};

type ConfigForm = {
  id?: string;
  bts_code: string;
  bts_specialite: string;
  session_examen: number;
  etablissement_nom: string;
  rne: string;
  logo_url: string;
  voie_formation: VoieFormation;
  template_storage_path: string;
  cachet_storage_path: string;
  sessions_precedentes: SessionPrecedenteRow[];
};

const defaultSessionsPrec = (sessionExamen: number): SessionPrecedenteRow[] => [
  { annee: sessionExamen - 1, presentes: 0, recus: 0 },
  { annee: sessionExamen - 2, presentes: 0, recus: 0 },
  { annee: sessionExamen - 3, presentes: 0, recus: 0 },
];

const emptyForm = (): ConfigForm => {
  const session = new Date().getFullYear() + 1;
  return {
    bts_code: '',
    bts_specialite: '',
    session_examen: session,
    etablissement_nom: 'CFA FormaVerse',
    rne: '',
    logo_url: '',
    voie_formation: 'apprentissage',
    template_storage_path: '',
    cachet_storage_path: '',
    sessions_precedentes: defaultSessionsPrec(session),
  };
};

const VOIE_LABELS: Record<VoieFormation, string> = {
  apprentissage: 'Apprentissage',
  initial: 'Formation initiale',
  formation_continue: 'Formation continue',
};

const AdminEtablissement = () => {
  const [configs, setConfigs] = useState<LivretConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<ConfigForm>(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [flash, setFlash] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [availableTemplates, setAvailableTemplates] = useState<string[]>([]);
  const [uploadingCachet, setUploadingCachet] = useState(false);
  const [cachetPreview, setCachetPreview] = useState<string | null>(null);

  useEffect(() => {
    void load();
    void loadAvailableTemplates();
  }, []);

  const load = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('livret_config')
        .select('*')
        .order('session_examen', { ascending: false })
        .order('bts_code');
      if (error) throw error;
      setConfigs((data ?? []) as LivretConfig[]);
    } catch (e) {
      console.error('Error loading livret_config:', e);
      setFlash({ type: 'error', text: 'Erreur de chargement.' });
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailableTemplates = async () => {
    try {
      const { data, error } = await supabase.storage.from('livret-templates').list('', {
        limit: 100,
      });
      if (error) throw error;
      const files = (data ?? [])
        .filter((f) => f.name && !f.name.startsWith('.'))
        .map((f) => f.name);
      setAvailableTemplates(files);
    } catch (e) {
      console.error('Cannot list templates:', e);
    }
  };

  const loadCachetPreview = async (path: string | null | undefined): Promise<void> => {
    if (!path) {
      setCachetPreview(null);
      return;
    }
    try {
      const { data, error } = await supabase.storage
        .from('livret-templates')
        .createSignedUrl(path, 60 * 60);
      if (error) throw error;
      setCachetPreview(data?.signedUrl ?? null);
    } catch {
      setCachetPreview(null);
    }
  };

  const openCreate = () => {
    setForm(emptyForm());
    setCachetPreview(null);
    setShowForm(true);
    setFlash(null);
  };

  const openEdit = (c: LivretConfig) => {
    const configured = Array.isArray(c.sessions_precedentes) ? c.sessions_precedentes : [];
    const merged: SessionPrecedenteRow[] = [];
    const defaults = defaultSessionsPrec(c.session_examen);
    for (let i = 0; i < 3; i++) {
      merged.push(
        configured[i] ?? defaults[i]
      );
    }
    setForm({
      id: c.id,
      bts_code: c.bts_code,
      bts_specialite: c.bts_specialite,
      session_examen: c.session_examen,
      etablissement_nom: c.etablissement_nom,
      rne: c.rne ?? '',
      logo_url: c.logo_url ?? '',
      voie_formation: c.voie_formation,
      template_storage_path: c.template_storage_path,
      cachet_storage_path: c.cachet_storage_path ?? '',
      sessions_precedentes: merged,
    });
    void loadCachetPreview(c.cachet_storage_path);
    setShowForm(true);
    setFlash(null);
  };

  const uploadCachet = async (file: File): Promise<void> => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setFlash({ type: 'error', text: 'Le cachet doit être une image (PNG, JPG).' });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setFlash({ type: 'error', text: 'Le cachet ne doit pas dépasser 2 Mo.' });
      return;
    }
    setUploadingCachet(true);
    setFlash(null);
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? 'png';
      const safeCode = (form.bts_code || 'bts').toLowerCase().replace(/[^a-z0-9]/g, '');
      const path = `cachets/cachet_${safeCode}_${form.session_examen}_${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from('livret-templates')
        .upload(path, file, { cacheControl: '3600', upsert: true });
      if (error) throw error;
      setForm((f) => ({ ...f, cachet_storage_path: path }));
      await loadCachetPreview(path);
      setFlash({ type: 'success', text: 'Cachet uploadé.' });
    } catch (e) {
      setFlash({
        type: 'error',
        text: `Upload cachet échoué : ${e instanceof Error ? e.message : 'erreur inconnue'}`,
      });
    } finally {
      setUploadingCachet(false);
    }
  };

  const removeCachet = (): void => {
    setForm((f) => ({ ...f, cachet_storage_path: '' }));
    setCachetPreview(null);
  };

  const updateSessionRow = (index: number, field: keyof SessionPrecedenteRow, value: number): void => {
    setForm((f) => {
      const next = [...f.sessions_precedentes];
      next[index] = { ...next[index], [field]: value };
      return { ...f, sessions_precedentes: next };
    });
  };

  // Quand la session d'examen change, on réinitialise les années des sessions précédentes
  // SEULEMENT si les valeurs sont encore à 0 (on ne détruit pas des données saisies).
  const onSessionChange = (newSession: number): void => {
    setForm((f) => {
      const allZero = f.sessions_precedentes.every((s) => s.presentes === 0 && s.recus === 0);
      return {
        ...f,
        session_examen: newSession,
        sessions_precedentes: allZero
          ? defaultSessionsPrec(newSession)
          : f.sessions_precedentes,
      };
    });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.bts_code.trim() || !form.bts_specialite.trim() || !form.template_storage_path.trim()) {
      setFlash({
        type: 'error',
        text: 'Code BTS, spécialité et chemin du template sont obligatoires.',
      });
      return;
    }
    setSubmitting(true);
    setFlash(null);
    try {
      const payload = {
        bts_code: form.bts_code.trim().toUpperCase(),
        bts_specialite: form.bts_specialite.trim(),
        session_examen: form.session_examen,
        etablissement_nom: form.etablissement_nom.trim(),
        rne: form.rne.trim() || null,
        logo_url: form.logo_url.trim() || null,
        voie_formation: form.voie_formation,
        template_storage_path: form.template_storage_path.trim(),
        cachet_storage_path: form.cachet_storage_path.trim() || null,
        sessions_precedentes: form.sessions_precedentes.map((s) => ({
          annee: Number(s.annee) || 0,
          presentes: Number(s.presentes) || 0,
          recus: Number(s.recus) || 0,
        })),
        updated_at: new Date().toISOString(),
      };
      if (form.id) {
        const { error } = await supabase.from('livret_config').update(payload).eq('id', form.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('livret_config').insert(payload);
        if (error) throw error;
      }
      setFlash({ type: 'success', text: 'Configuration enregistrée.' });
      setShowForm(false);
      await load();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur inconnue';
      setFlash({ type: 'error', text: `Échec : ${msg}` });
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (c: LivretConfig) => {
    if (
      !window.confirm(
        `Supprimer la configuration ${c.bts_code} session ${c.session_examen} ?\n\nCela n'affectera pas les livrets déjà générés.`
      )
    )
      return;
    try {
      const { error } = await supabase.from('livret_config').delete().eq('id', c.id);
      if (error) throw error;
      setConfigs((prev) => prev.filter((x) => x.id !== c.id));
      setFlash({ type: 'success', text: 'Configuration supprimée.' });
    } catch (e) {
      setFlash({
        type: 'error',
        text: e instanceof Error ? e.message : 'Erreur de suppression.',
      });
    }
  };

  return (
    <div className="animate-fade-in">
      <header className="mb-8">
        <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-2">
          <ChevronLeft className="h-4 w-4 mr-1" />
          Retour au tableau de bord
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <School className="h-7 w-7 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-900">Établissement & Livrets</h1>
        </div>
        <p className="text-gray-600">
          Configurations des livrets scolaires par diplôme et session d'examen. Chaque configuration
          relie un BTS à un template Excel officiel dans le Storage.
        </p>
      </header>

      {flash && (
        <div
          className={`mb-6 p-4 rounded-md flex items-start ${
            flash.type === 'success'
              ? 'bg-success-50 border border-success-200 text-success-700'
              : 'bg-error-50 border border-error-200 text-error-700'
          }`}
        >
          {flash.type === 'success' ? (
            <CheckCircle className="h-5 w-5 mr-2 mt-0.5" />
          ) : (
            <AlertCircle className="h-5 w-5 mr-2 mt-0.5" />
          )}
          <span className="text-sm">{flash.text}</span>
        </div>
      )}

      <div className="mb-4 flex justify-between items-center">
        <p className="text-sm text-gray-600">
          {configs.length} configuration{configs.length > 1 ? 's' : ''} enregistrée
          {configs.length > 1 ? 's' : ''}
        </p>
        <button onClick={openCreate} className="btn btn-primary inline-flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Nouvelle configuration
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6 border-l-4 border-primary-500">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900">
              {form.id ? 'Modifier la configuration' : 'Nouvelle configuration livret'}
            </h2>
            <button
              onClick={() => setShowForm(false)}
              className="text-gray-400 hover:text-gray-700 text-sm"
            >
              Fermer
            </button>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code BTS *
                </label>
                <input
                  type="text"
                  value={form.bts_code}
                  onChange={(e) => setForm({ ...form, bts_code: e.target.value.toUpperCase() })}
                  placeholder="NDRC, MCO, SAM..."
                  maxLength={20}
                  className="input-field w-full font-mono uppercase"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Spécialité (libellé officiel) *
                </label>
                <input
                  type="text"
                  value={form.bts_specialite}
                  onChange={(e) => setForm({ ...form, bts_specialite: e.target.value })}
                  placeholder="Négociation et Digitalisation de la Relation Client"
                  className="input-field w-full"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Session d'examen *
                </label>
                <input
                  type="number"
                  value={form.session_examen}
                  onChange={(e) => onSessionChange(Number(e.target.value))}
                  min="2020"
                  max="2050"
                  className="input-field w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Voie de formation
                </label>
                <select
                  value={form.voie_formation}
                  onChange={(e) =>
                    setForm({ ...form, voie_formation: e.target.value as VoieFormation })
                  }
                  className="input-field w-full"
                >
                  {(Object.keys(VOIE_LABELS) as VoieFormation[]).map((v) => (
                    <option key={v} value={v}>
                      {VOIE_LABELS[v]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code RNE
                </label>
                <input
                  type="text"
                  value={form.rne}
                  onChange={(e) => setForm({ ...form, rne: e.target.value.toUpperCase() })}
                  placeholder="0831234X"
                  maxLength={8}
                  className="input-field w-full font-mono uppercase"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom de l'établissement *
              </label>
              <input
                type="text"
                value={form.etablissement_nom}
                onChange={(e) => setForm({ ...form, etablissement_nom: e.target.value })}
                className="input-field w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Excel (bucket <code className="text-xs bg-gray-100 px-1 rounded">livret-templates</code>) *
              </label>
              {availableTemplates.length > 0 ? (
                <select
                  value={form.template_storage_path}
                  onChange={(e) => setForm({ ...form, template_storage_path: e.target.value })}
                  className="input-field w-full font-mono"
                  required
                >
                  <option value="">— Choisir un fichier —</option>
                  {availableTemplates.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={form.template_storage_path}
                  onChange={(e) => setForm({ ...form, template_storage_path: e.target.value })}
                  placeholder="livret_bts_ndrc_2026.xlsx"
                  className="input-field w-full font-mono"
                  required
                />
              )}
              <p className="text-xs text-gray-500 mt-1 flex items-center">
                <Upload className="h-3 w-3 mr-1" />
                Pour uploader un nouveau template, utilisez le dashboard Supabase →
                Storage → livret-templates.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                URL du logo (optionnel)
              </label>
              <input
                type="url"
                value={form.logo_url}
                onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                placeholder="https://..."
                className="input-field w-full"
              />
            </div>

            {/* Cachet de l'établissement */}
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Stamp className="h-4 w-4 text-primary-600" />
                Cachet / signature de l'établissement
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Image PNG ou JPG, 2 Mo max. Apparaîtra en haut à droite et en bas à droite
                du livret PDF, avec la date de génération.
              </p>
              <div className="flex items-start gap-4">
                {cachetPreview ? (
                  <div className="relative w-32 h-32 border border-gray-300 rounded-md bg-gray-50 flex items-center justify-center overflow-hidden">
                    <img
                      src={cachetPreview}
                      alt="Cachet"
                      className="max-w-full max-h-full object-contain"
                    />
                    <button
                      type="button"
                      onClick={removeCachet}
                      className="absolute top-1 right-1 bg-white rounded-full p-0.5 text-gray-400 hover:text-error-600 shadow"
                      title="Retirer le cachet"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center text-gray-400">
                    <Stamp className="h-8 w-8" />
                  </div>
                )}
                <div className="flex-1">
                  <label className="btn btn-outline inline-flex items-center gap-2 cursor-pointer">
                    {uploadingCachet ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    {form.cachet_storage_path ? 'Remplacer le cachet' : 'Uploader un cachet'}
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      className="hidden"
                      disabled={uploadingCachet}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void uploadCachet(file);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {form.cachet_storage_path && (
                    <p className="text-xs text-gray-500 mt-2 font-mono break-all">
                      {form.cachet_storage_path}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Sessions précédentes */}
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Résultats des sessions précédentes
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Trois dernières années. Si c'est votre première session, laissez les valeurs à 0.
                Le % de réussite est calculé automatiquement sur le livret (Reçus / Présentés).
              </p>
              <table className="data-table text-sm">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left">Année</th>
                    <th className="px-3 py-2 text-left">Présentés</th>
                    <th className="px-3 py-2 text-left">Reçus</th>
                    <th className="px-3 py-2 text-left">% Réussite</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {form.sessions_precedentes.map((s, i) => {
                    const pct = s.presentes > 0
                      ? `${Math.round((s.recus / s.presentes) * 100)}%`
                      : '—';
                    return (
                      <tr key={i}>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            value={s.annee}
                            onChange={(e) => updateSessionRow(i, 'annee', Number(e.target.value))}
                            min="2000"
                            max="2050"
                            className="input-field w-24"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            value={s.presentes}
                            onChange={(e) => updateSessionRow(i, 'presentes', Number(e.target.value))}
                            min="0"
                            className="input-field w-24"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            value={s.recus}
                            onChange={(e) => updateSessionRow(i, 'recus', Number(e.target.value))}
                            min="0"
                            max={s.presentes}
                            className="input-field w-24"
                          />
                        </td>
                        <td className="px-3 py-2 text-gray-600 font-medium">{pct}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="btn btn-outline"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="btn btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {form.id ? 'Mettre à jour' : 'Créer'}
              </button>
            </div>
          </form>
        </div>
      )}

      {isLoading ? (
        <div className="p-12 text-center text-gray-500">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary-600" />
        </div>
      ) : configs.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <p>Aucune configuration de livret enregistrée.</p>
          <p className="text-sm mt-2">
            Créez une première configuration pour relier un BTS à son template Excel officiel.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th className="px-4 py-2">BTS</th>
                <th className="px-4 py-2">Session</th>
                <th className="px-4 py-2">Établissement</th>
                <th className="px-4 py-2">Voie</th>
                <th className="px-4 py-2">Template</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {configs.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div className="font-semibold text-gray-900">{c.bts_code}</div>
                    <div className="text-xs text-gray-500">{c.bts_specialite}</div>
                  </td>
                  <td className="font-medium">{c.session_examen}</td>
                  <td>
                    <div>{c.etablissement_nom}</div>
                    {c.rne && (
                      <div className="text-xs text-gray-500 font-mono">{c.rne}</div>
                    )}
                  </td>
                  <td className="text-sm">{VOIE_LABELS[c.voie_formation]}</td>
                  <td className="text-xs font-mono text-gray-600">
                    {c.template_storage_path}
                  </td>
                  <td className="text-right">
                    <div className="inline-flex gap-2">
                      <button
                        onClick={() => openEdit(c)}
                        className="p-1.5 text-gray-400 hover:text-primary-600 transition"
                        title="Modifier"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => remove(c)}
                        className="p-1.5 text-gray-400 hover:text-error-600 transition"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminEtablissement;
